/**
 * Client side of POST /api/save-transcription, shared by the transcription hook and
 * the History "Complete" button so both go through the same charge-once key.
 *
 * No clientRef is sent: the server's client_ref column is a UUID, so a `hash:lang` key was
 * always discarded. Charge-once is enforced server-side on (authenticated user,
 * content_hash, language, 24 h) via the lp_charge_once RPC.
 */

/** Files above this size are not hashed: arrayBuffer() would hold the whole file in memory. */
export const MAX_HASH_BYTES = 250 * 1024 * 1024;

/**
 * Hex SHA-256 of the file's bytes. Resolves undefined when SubtleCrypto is unavailable or
 * the file is over MAX_HASH_BYTES (no hash = no charge-once = today's behaviour, no OOM).
 */
export async function sha256Hex(blob: Blob): Promise<string | undefined> {
  try {
    if (blob.size > MAX_HASH_BYTES) return undefined;
    if (typeof crypto === 'undefined' || !crypto.subtle) return undefined;
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

export interface SaveTranscriptionPayload {
  title?: string;
  transcript: string;
  intelligence?: string | null;
  speaker_map?: string | null;
  duration_seconds: number;
  engine: string;
  confidence?: number;
  quality_grade?: string | null;
  utterances?: unknown;
  language?: string | null;
  /** Hex SHA-256 of the audio; omitted when unknown or the file is too large to hash. */
  contentHash?: string;
  /** AssemblyAI job id — updates that job's 'processing' stub instead of inserting. */
  assemblyaiId?: string;
}

export function postSaveTranscription(token: string, payload: SaveTranscriptionPayload): Promise<Response> {
  const { contentHash, assemblyaiId, ...rest } = payload;
  return fetch('/api/save-transcription', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...rest,
      ...(contentHash ? { contentHash } : {}),
      ...(assemblyaiId ? { assemblyaiId } : {}),
    }),
  });
}
